import FolderItem     from "../Entities/FolderItem";
import CollectionList from '@/js/Shared/Collections/CollectionList';

export { FoldersCollection as default };

/**
* Folder IndexEntity
*
* Defines folders index struture
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class FoldersCollection extends CollectionList {
  /**
   * Folders parent/child tree
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  tree = {
    parents : [],
    childs  : {},
  }

  /**
   * Unread Count
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  unread = 0;

  /**
   * Sets the id
   *
   * @public
   *
   * @since 7.0.0
   */
  constructor() {
    super('id');
  }

  /**
   * Returns an Item by id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} id id
   *
   * @return {FolderItem}
   */
  get(id) {
    return this.index[id] || new FolderItem();
  }

  /**
   * Adss a folder entity to index
   *
   * @public
   *
   * @param {FolderItem} folder folder entity
   *
   * @since 7.0.0
   */
  add(folder) {
    if (super.add(folder) === true) {
      this.addToTree(folder)
    }
  }

  /**
   * Removes a folder id from index
   *
   * @todo add/remove items from tree and childs needs a new system, this is a mess!!
   *
   * @public
   *
   * @param {FolderItem} folder
   *
   * @since 7.0.0
   */
  delete(folder_id) {
    if (!this.index[folder_id]) {
      return;
    }

    var index = this.tree.parents.indexOf(this.index[folder_id]);

    if (index != -1) {
      this.tree.parents.splice(index, 1);
    }

    if (this.index[folder_id].parent) {
      var index2 = this.tree.childs[this.index[folder_id].parent].indexOf(this.index[folder_id]);

      if (index2 != -1) {
        this.tree.childs[this.index[folder_id].parent].splice(index2, 1);

        if (this.tree.childs[this.index[folder_id].parent].length === 0) {
          delete this.tree.childs[this.index[folder_id].parent];
        }
      }
    }

    super.delete(folder_id);
  }

  /**
   * Adds folder to parent/child tree
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder
   *
   * @return {void}
   */
  addToTree(folder) {
    if (!folder.parent) {
      this.tree.parents.push(folder);
      return;
    }

    if (!this.tree.childs[folder.parent]) {
      this.tree.childs[folder.parent] = [];
    }

    this.tree.childs[folder.parent].push(folder);
  }

  /**
   * Finds all childs and subchilds of fiven folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder
   *
   * @return {Array<FolderItem>}
   */
  getChilds(folder) {
    if (!this.tree.childs[folder.id]) {
      return [];
    }

    return this.buildChilds(folder, []);
  }

  /**
   * Recursevly populates an array with all childs in the tree of a given folder
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {FolderItem}         folder folder
   * @param {Array<FolderEntity} childs array to be populated with childs
   *
   * @return {Array.<FolderEntity>}
   */
  buildChilds(folder, childs) {
    if (!this.tree.childs[folder.id]) {
      return;
    }

    this.tree.childs[folder.id].forEach(child => {
      childs.push(child);
      this.buildChilds(child, childs);
    });

    return childs;
  }

  /**
   * Recalculates unread count from all items
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  recalculateUnread() {
    this.unread = 0;

    this.items.forEach((folder) => {
      this.unread += folder.unread;
    })
  }

  /**
   * Recalculates unread count from all items
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  recalculateChildsUnread() {
    this.items.forEach((folder) => {
      folder.childs_unread = this.countChildsUnread(folder, 0);
    })
  }

  /**
   * Recursevely counts unread from all childs in the tree
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {FolderEntity} folder folder entity
   * @param {Number}       unread unread count
   *
   * @return {void}
   */
  countChildsUnread(folder, unread) {
    if (!this.tree.childs[folder.id]) {
      return unread;
    }

    this.tree.childs[folder.id].forEach(child => {
      unread += child.unread;
      unread = this.countChildsUnread(child, unread);
    });

    return unread;
  }

  /**
   * Recursevely adds child items in the tree to items
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {FolderEntity} folder folder entity
   *
   * @return {void}
   */
  addChildsToItems(folder, unread) {
    if (!this.tree.childs[folder.id]) {
      folder.childs        = false;
      return;
    }

    folder.childs = true;

    this.tree.childs[folder.id].forEach(child => {
      this.items.push(child);
      return this.addChildsToItems(child, unread);
    });
  }

  /**
   * Resorts all items in the tree
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resortItems() {
    this.items = [];

    this.sortAlphabetical(this.tree.parents);

    for (let id in this.tree.childs) {
      this.sortAlphabetical(this.tree.childs[id]);
    }

    this.tree.parents.forEach((folder) => {
      this.items.push(folder);

      this.addChildsToItems(folder, 0)
    })

    this.count = this.items.length;
  }


  /**
   * Sorts items in alphabetical order
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Array} array array to be sorted
   *
   * @return {void}
   */
  sortAlphabetical(array) {
    array.sort((a,b) => {
      if (a.main || b.main) {
        return 0;
      }

      return a.name.localeCompare(b.name, undefined, { numeric: true });
    })
  }
}